<script lang="ts">
import type {Prop, PropType} from "vue";
type InputValue = string | number | Date | null | undefined;
interface InputState {internalValue: InputValue; showPassword: boolean; customPadding: number; debouncedUpdate: (value: InputValue) => void; observer?: IntersectionObserver}
import {defineComponent} from "vue";
import {debounce} from "@/utils/PaginationUtil";
import CloseIcon from "@/assets/icons/CloseIcon.vue";
import EyeIcon from "@/assets/icons/EyeIcon.vue";
import LoadingIcon from "@/assets/icons/LoadingIcon.vue";
import {LoadingType} from "@/type/LoadingType";

export default defineComponent({
  name: "Input",
  computed: {
    LoadingType() {
      return LoadingType
    }
  },
  components: {LoadingIcon, EyeIcon, CloseIcon},

  props: {
    value: {
      validator(value: unknown) {
        return (
            value === null ||
            value instanceof Date ||
            typeof value === "string" ||
            typeof value === "number"
        );
      },
    } as Prop<InputValue>,

    label: {
      type: String,
      default: "",
    },

    placeholder: {
      type: String,
      default: "",
    },

    type: {
      type: String,
      default: "text",
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    debounceTime: {
      type: Number,
      default: 250,
    },

    containerClass: {
      type: String,
      default: "",
    },

    inputClass: {
      type: String,
      default: "",
    },

    inputStyle: {
      type: String,
      default: "",
    },

    textArea: {
      type: Boolean,
      default: false,
    },

    textAreaRows: {
      type: Number,
      default: 3,
    },

    errorMessage: {
      type: String,
      default: "",
    },

    errorClass: {
      type: String,
      default: "",
    },

    clearIcon: {
      type: Boolean,
      default: false,
    },

    autocomplete: {
      type: Boolean,
      default: false,
    },

    unit: {
      type: String,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    translationInput: {
      type: Boolean,
      default: false,
    }
  },

  data() {
    return {
      internalValue: this.value,

      showPassword: false,

      customPadding: 0,
    } as InputState;
  },

  watch: {
    value(newValue) {
      this.internalValue = newValue;
    },

    internalValue(newValue) {
      const outputValue =
          this.type === "number" && newValue !== "" ? Number(newValue) : newValue;
      this.debouncedUpdate(outputValue);

      this.updatePadding();
    },
  },

  created() {
    this.debouncedUpdate = debounce((value: InputValue) => {
      this.$emit("update:value", value);
    }, this.debounceTime);
  },

  methods: {
    focus() {
      ;(this.$refs.input as HTMLTextAreaElement).focus();
    },

    togglePassword() {
      this.showPassword = !this.showPassword;
    },

    updatePadding() {
      this.$nextTick(() => {
        const unitEl = (this.$refs.unit as HTMLElement);
        const iconsEl = (this.$refs.inputIcons as HTMLElement);

        const unitWidth = unitEl ? unitEl.offsetWidth : 0;
        const iconsWidth = iconsEl ? iconsEl.offsetWidth : 0;

        this.customPadding = Math.max(unitWidth, iconsWidth);
      });
    }
  },

  mounted() {
    this.updatePadding();
    window.addEventListener("resize", this.updatePadding);
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.updatePadding);
    if (this.observer) this.observer.disconnect();
  },
});
</script>

<template>
  <div
      class="custom-input-container position-relative"
  >
    <div class="custom-input-box d-flex justify-content-between">
      <div class="custom-input-label" v-if="label || $slots.label">
        <slot name="label"/>
        {{ label }}
      </div>
      <div v-if="errorMessage" class="error-message">({{ errorMessage }})</div>
    </div>

    <div class="position-relative">
      <input
          v-if="!textArea"
          ref="input"
          class="custom-input"
          :class="[
          inputClass,
          !loading && errorMessage ? (errorClass ? errorClass : 'error') : '',
          !loading && errorMessage && !translationInput ? 'error-input-field' : '',
          clearIcon ? 'icon' : '',
          disabled || loading ? 'disabled' : ''
        ]"
          :type="type === 'password' ? (showPassword ? 'text' : 'password') : type"
          :placeholder="placeholder"
          :disabled="disabled || loading"
          :style="[inputStyle, { paddingRight: customPadding + 'px' }]"
          v-model="internalValue as string | number | null"
          @input="$emit('input')"
          :autocomplete="autocomplete as unknown as string"
      />

      <textarea
          v-else
          ref="input"
          :rows="textAreaRows"
          class="custom-textarea"
          :class="[
          !loading && errorMessage ? (errorClass ? errorClass : 'error') : '',
          disabled ? 'disabled' : '',
          inputClass
        ]"
          :placeholder="placeholder"
          :disabled="disabled"
          :style="inputStyle"
          v-model="internalValue as string | number | null"
          @input="$emit('input')"
      />

      <LoadingIcon
          v-if="loading"
          :type="LoadingType.SMALL"
          class="input-loader"
      />

      <div v-if="unit"
           class="unit"
           :class="{'align-items-start pt-2': textArea}"
           ref="unit"
      >
        {{ unit }}
      </div>

      <div
          class="input-icons"
          :class="{'icons-textarea': textArea, 'icons-input': !textArea}"
          ref="inputIcons"
      >
        <slot name="icons"/>

        <CloseIcon
            class="cursor-pointer icon-white"
            @click="$emit('clear')"
            v-if="clearIcon && internalValue"
        />

        <div v-if="type === 'password' && internalValue" @click="togglePassword" class="cursor-pointer mb-1">
          <EyeIcon :is-active="showPassword"/>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.dropdown {
  background: rgba(68, 71, 72, 1) !important;
}

.input-loader {
  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;

  display: flex;
  align-items: center !important;
  justify-content: start !important;

  padding: 0 10px;

  pointer-events: none;
  z-index: 2;
}

.unit {
  font-family: "Artifex CF Light", serif;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);

  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  display: flex;
  align-items: center;
  padding: 0 10px;
  pointer-events: none;
}

.input-icons {
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
  padding: 0 5px;

  &.icons-input {
    top: 0;
    height: 100%;
  }

  &.icons-textarea {
    top: 5px;
    height: auto;
  }

  > *:not(:last-child) {
    margin-right: 8px;
  }
}

.custom-input,
.custom-textarea {
  width: 100%;
  font-family: "Artifex CF Regular", serif;
  font-size: 12px;
  background: rgba(68, 71, 72, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  color: rgba(255, 255, 255, 0.9);
  transition: all 0.3s ease;
  padding: 8px 10px;
  line-height: 12px;

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    background: rgba(54, 77, 87, 0.4);
    border-color: rgba(184, 232, 255, 1);
    color: rgba(184, 232, 255, 1);
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.25) inset,
    0 2px 7px -2px rgba(10, 17, 21, 0.25);
    outline: none;
  }

  &:hover:not(:focus) {
    background: rgba(68, 71, 72, 0.4);
    border-color: rgba(255, 255, 255, 0.4);
    color: rgba(255, 255, 255, 0.9);
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.25) inset,
    0 2px 7px -2px rgba(10, 17, 21, 0.25);
  }

  &.error {
    border-color: rgba(255, 0, 0, 0.6) !important;
  }

  &.disabled {
    cursor: not-allowed !important;
    opacity: 0.5;
  }
}

.custom-input {
  height: 30px;
  position: relative;

  &.icon {
    padding-right: 25px;
  }
}

.custom-textarea {
  height: auto;
  resize: vertical;
  min-height: 30px;
  position: relative;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button,
textarea::-webkit-outer-spin-button,
textarea::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type=number] {
  -moz-appearance: textfield;
}

input[type=color] {
  padding: 4px !important;

  cursor: pointer;
}

.custom-input-label {
  font-family: "Artifex CF Light", serif;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  display: block;
}

.icon-white {
  filter: invert(100%) sepia(4%) saturate(464%) hue-rotate(136deg) brightness(88%) contrast(77%);
}
</style>
